import type {
  CrossroadsLog,
  GroupMetadata,
} from '@/api/hooks/useIntegrationLogs'
import { retryConnection } from '@/api/hooks/useIntegrationLogs/mutations'
import { retrySupplyPlanBatch } from '@/api/hooks/useSupplyPlans'
import { friendlySagaName, hasTranslationAudit } from '@/constants/sagaTypes'
import {
  CloseOutlined,
  PartitionOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import {
  GraviButton,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import { useState } from 'react'
import { useLinkClickHandler } from 'react-router-dom'
import { AuditModal } from './AuditModal/AuditModal'
import { LogDetailsPanel } from './LogDetailsPanel'

interface LogDetailsSidebarProps {
  selectedLog: CrossroadsLog | undefined | null
  groupMetadata: GroupMetadata | undefined | null
  selectedTenantName: string | undefined | null
  logGroupId: string | null
  refetchLogs: () => void | Promise<unknown>
  onClose: () => void
  /** Set on the Supply Plan Integration Logs page — this batch's id, used
   * as the Retry Batch button's target (no navigation link here; that's a
   * grid column now). */
  supplyPlanBatchId?: string | null
  /** Set on the Supply Plans page — links a row's batch back over to its
   * Supply Plan Integration Logs, filtered to just it. */
  integrationLogsBatchId?: string | null
}

export function LogDetailsSidebar({
  selectedLog,
  groupMetadata,
  selectedTenantName,
  logGroupId,
  refetchLogs,
  onClose,
  supplyPlanBatchId,
  integrationLogsBatchId,
}: LogDetailsSidebarProps) {
  const [isAuditOpen, setIsAuditOpen] = useState(false)
  const integrationLogsUrl = integrationLogsBatchId
    ? `/integration-management/supply-plan-logs?batch_id=${encodeURIComponent(integrationLogsBatchId)}`
    : ''
  const _handleIntegrationLogsClick =
    useLinkClickHandler<HTMLElement>(integrationLogsUrl)
  // Prefer the batch id straight off the selected log's own metadata --
  // supply_plan_batch_summary and supply_plan logs both carry their own
  // batch_id (see shared/models/logs.py), so this is correct regardless of
  // how the log was selected. That matters because selectedGroupMetadata
  // (which supplyPlanBatchId is derived from, on the Supply Plan Integration
  // Logs page) is only ever set by clicking the master row -- useLogSidebar's
  // selectLog, used when clicking a row inside the expanded detail grid
  // (useLogDetailGrid's onRowClicked), never populates it. Without this
  // fallback-first ordering, a failed log opened via the detail grid alone
  // has no batch id to retry against and the Retry Batch button silently
  // doesn't render even though the log otherwise qualifies.
  const logMetadataBatchId =
    selectedLog?.metadata?.type === 'supply_plan_batch_summary' ||
    selectedLog?.metadata?.type === 'supply_plan'
      ? selectedLog.metadata.batch_id
      : null
  const batchId =
    logMetadataBatchId ?? supplyPlanBatchId ?? integrationLogsBatchId

  async function handleRetry() {
    if (!selectedLog || !selectedTenantName || !logGroupId) return

    await retryConnection(selectedTenantName, selectedLog, logGroupId)
    await refetchLogs()
  }

  async function handleRetryBatch() {
    if (!selectedLog || !batchId) return

    const result = await retrySupplyPlanBatch(selectedLog.routing, batchId)
    if (result.success) await refetchLogs()
  }

  if (!selectedLog) return null

  const sagaName = friendlySagaName(selectedLog.saga_type)
  // Same preconditions as the detail grid's audit button -- an audit is keyed
  // by (log group, saga), so both ids have to be in hand, and only the sagas
  // that translate leave one behind.
  const auditSagaId = selectedLog.saga_id
  const canViewAudit =
    !!auditSagaId && !!logGroupId && hasTranslationAudit(selectedLog.saga_type)
  // Retried and overridden both mean this log has been superseded, so there's
  // nothing left to retry -- same reason the grids gray those rows out.
  const isSuperseded = selectedLog.retried || selectedLog.overridden

  return (
    // 0.75 against the grid's flex={2.5} puts the sidebar at ~23% of the
    // width, down from ~29% at flex={1}.
    <Vertical flex={0.75} className='h-full' bg='elevated' border='left'>
      <Horizontal
        style={{
          borderBottom: '1px solid var(--border-color)',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        className='mt-4 mx-4'
      >
        <Texto category='h4' weight='600'>
          Log Details
        </Texto>
        <Horizontal gap={8} alignItems='center'>
          {canViewAudit && (
            <Tooltip title='View mapping audit'>
              <GraviButton
                icon={<PartitionOutlined />}
                onClick={() => setIsAuditOpen(true)}
              />
            </Tooltip>
          )}
          {selectedLog?.metadata?.type === 'order' &&
            selectedLog?.status === 'failed' &&
            !isSuperseded && (
              <GraviButton
                appearance='solid'
                icon={<ReloadOutlined />}
                onClick={handleRetry}
              >
                {sagaName ? `Retry ${sagaName}` : 'Retry Task'}
              </GraviButton>
            )}
          {(selectedLog?.metadata?.type === 'supply_plan_batch_summary' ||
            selectedLog?.metadata?.type === 'supply_plan') &&
            selectedLog?.status === 'failed' &&
            !isSuperseded &&
            batchId && (
              <GraviButton
                appearance='solid'
                icon={<ReloadOutlined />}
                onClick={handleRetryBatch}
              >
                Retry Batch
              </GraviButton>
            )}
          <CloseOutlined
            onClick={onClose}
            style={{
              cursor: 'pointer',
              fontSize: 16,
              color: 'var(--theme-text-secondary, #666)',
            }}
          />
        </Horizontal>
      </Horizontal>
      <Vertical style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        <Horizontal gap={24}>
          <LogDetailsPanel log={selectedLog} groupMetadata={groupMetadata} />
        </Horizontal>
      </Vertical>
      {isAuditOpen && canViewAudit && (
        <AuditModal
          open
          onClose={() => setIsAuditOpen(false)}
          title={sagaName ?? selectedLog.title ?? 'Audit'}
          logGroupId={logGroupId}
          sagaId={auditSagaId}
          routing={selectedLog.routing}
        />
      )}
    </Vertical>
  )
}
